import { Video } from '../types';
import { ChannelInfo, ChannelTotals } from '../hooks/useChannel';
import { formatViewCount } from '../utils/format';
import { VideoGrid } from './VideoGrid';

interface ChannelPageProps {
  channelId: string;
  channel: ChannelInfo | null;
  title: string | null;
  totals: ChannelTotals | null;
  videos: Video[];
  onPlayVideo: (video: Video) => void;
}

/**
 * Dedicated landing page for a single channel: /channel/{id}. Live metadata
 * (avatar, banner, subscribers) comes from YouTube; the ranked video list
 * comes from our own tracked history in D1, so the page still renders even
 * if the live lookup fails or the channel hides its subscriber count.
 */
export function ChannelPage({ channelId, channel, title, totals, videos, onPlayVideo }: ChannelPageProps) {
  const displayTitle = channel?.title || title;
  if (!displayTitle) return null;

  const channelUrl = channel?.customUrl
    ? `https://www.youtube.com/${channel.customUrl}`
    : `https://www.youtube.com/channel/${channelId}`;

  return (
    <div className="channel-page">
      {channel?.bannerUrl && (
        <div className="channel-banner channel-page-banner">
          <img src={`${channel.bannerUrl}=w1060`} alt="" loading="lazy" />
        </div>
      )}

      <div className="channel-header channel-page-header">
        {channel?.thumbnailUrl && (
          <img className="channel-avatar" src={channel.thumbnailUrl} alt={displayTitle} />
        )}
        <div className="channel-heading">
          <h2 className="channel-title">{displayTitle}</h2>
          {channel?.customUrl && <p className="channel-handle">{channel.customUrl}</p>}
          <div className="channel-stats">
            {channel && channel.subscriberCount !== null && (
              <span>{formatViewCount(channel.subscriberCount)} subscribers</span>
            )}
            <span>{formatViewCount(channel?.videoCount ?? totals?.videoCount ?? 0)} videos tracked</span>
            <span>{formatViewCount(channel?.viewCount ?? totals?.totalViews ?? 0)} total views</span>
          </div>
        </div>
      </div>

      {channel?.description && <p className="channel-description">{channel.description}</p>}

      <a className="channel-visit-btn" href={channelUrl} target="_blank" rel="noopener noreferrer">
        Visit on YouTube ↗
      </a>

      {videos.length > 0 ? (
        <VideoGrid videos={videos} onPlayVideo={onPlayVideo} />
      ) : (
        <p className="results-intro" style={{ opacity: 0.7 }}>
          We're still gathering tracked history for this channel — check back soon.
        </p>
      )}
    </div>
  );
}
